import { useState, useEffect, useCallback } from "react";

import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import {
	useGetCompanyDealCreationTriggerQuery,
	useGetOAuthStatusQuery,
} from "@/pages/Private/redux/hubspot/hubspot.api";

import { FeaturesCodes } from "../../../../Users/enum/codes";
import {
	HubspotDealCreationTrigger,
	HubspotDealCreationTriggerWithInherit,
} from "../../../../Company/schema/hubspot";
import { List } from "../../../schema/list";

interface UseHubspotDealCreationTriggerOptions {
	listId: number;
	listData: List | undefined;
}

export const useHubspotDealCreationTrigger = ({
	listId,
	listData,
}: UseHubspotDealCreationTriggerOptions) => {
	const isHubspotEnabled = useIsFeatureActive(FeaturesCodes.HUBSPOT);
	const { user } = useAppSelector(authSelector);
	const hasHubspotApiKey = user?.company?.hasHubspotApiKey ?? false;
	const { data: oauthStatus } = useGetOAuthStatusQuery(undefined, {
		skip: !isHubspotEnabled,
	});
	const isConnectedOAuth = oauthStatus?.connected ?? false;
	const isConnected = hasHubspotApiKey || isConnectedOAuth;

	// Fetch company default for resolving INHERIT
	const { data: companyDealCreationTrigger } = useGetCompanyDealCreationTriggerQuery(undefined, {
		skip: !isHubspotEnabled || !isConnected,
	});

	// Local state for selected trigger
	const [selectedDealCreationTrigger, setSelectedDealCreationTrigger] = useState<
		HubspotDealCreationTriggerWithInherit | undefined
	>(undefined);

	// Initialize from list data
	useEffect(() => {
		if (listData?.hubspotDealCreationTrigger) {
			setSelectedDealCreationTrigger(listData.hubspotDealCreationTrigger);
		} else if (listData && !listData.hubspotDealCreationTrigger) {
			// Default to INHERIT if not set
			setSelectedDealCreationTrigger(HubspotDealCreationTriggerWithInherit.INHERIT);
		}
	}, [listData]);

	// Resolve the effective trigger when INHERIT is selected
	const effectiveTrigger: HubspotDealCreationTrigger | undefined =
		selectedDealCreationTrigger === HubspotDealCreationTriggerWithInherit.INHERIT
			? companyDealCreationTrigger?.trigger
			: selectedDealCreationTrigger !== undefined
				? (selectedDealCreationTrigger as unknown as HubspotDealCreationTrigger)
				: undefined;

	// Check if deal creation trigger has changed from initial value
	const hasDealCreationTriggerChanged = useCallback(() => {
		if (!isHubspotEnabled || !isConnected || selectedDealCreationTrigger === undefined) {
			return false;
		}

		const initialValue =
			listData?.hubspotDealCreationTrigger ?? HubspotDealCreationTriggerWithInherit.INHERIT;

		return selectedDealCreationTrigger !== initialValue;
	}, [isHubspotEnabled, isConnected, selectedDealCreationTrigger, listData]);

	return {
		// Feature flags
		isHubspotEnabled,
		isConnected,

		// Deal creation trigger state
		selectedDealCreationTrigger,
		setSelectedDealCreationTrigger,
		effectiveTrigger,
		companyDealCreationTrigger,

		// Actions
		hasDealCreationTriggerChanged,
	};
};
